import { Button, Dropdown, notification, Tooltip } from 'antd';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { MasterSeriesCanvas } from 'src/model';
import { UseCardExport, useCardExport, useLanguage, useMasterSeriDrawer, useSetting } from 'src/service';
import { GatewayOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useShallow } from 'zustand/react/shallow';
import { forceRefocus } from 'src/util';
import { ResolutionPicker } from 'src/component';

const ResolutionButton = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    padding: var(--spacing-xs) var(--spacing);
    border-left: var(--bw) solid var(--sub-secondary);
    border-radius: 0 var(--br-lg) var(--br-lg) 0;
    color: var(--color);
    cursor: pointer;
    .anticon {
        font-size: var(--fs-2xl);
        margin-left: 0;
    }
    .resolution-overlay {
        position: absolute;
        left: 50%;
        bottom: 3px; // Alignment
        font-size: var(--fs-xs);
        text-align: center;
        transform: translateX(-50%);
    }
    .resolution-icon {
        transform: translateY(-5px); // Alignment
    }
`;
const StyledDownloadButton = styled(Button)`
    padding: 0;
    flex: 1;
    font-size: var(--fs-xl);
    border: none;
    font-weight: 500;
    box-shadow: var(--bs-button);
    height: 39px; // Alignment
    line-height: 1;
    &:focus, &:active {
        color: white;
    }
    .button-label {
        height: 100%; // Alignment
        align-content: center;
        border-radius: var(--br-lg);
        line-height: 1.5;
        .label-content {
            padding: 4px 15px;
            border: var(--bw) solid transparent;
            border-radius: var(--br-lg);
            line-height: 29px; // Alignment
        }
    }
`;
export type DownloadPanelRef = {
    download: () => void,
    isPipelineRunning: () => boolean,
}
export type DownloadPanel = {
    imageChangeCount: number,
    globalScale: number,
    canvasMap: MasterSeriesCanvas,
    onTainted: () => void,
    onExportSucess: () => void,
} & Pick<UseCardExport, 'isTainted'
    | 'isInitializing'
    | 'onDownloadError'>;
export const DownloadPanel = forwardRef<DownloadPanelRef, DownloadPanel>(({
    isTainted,
    isInitializing,
    imageChangeCount,
    globalScale,
    canvasMap,
    onTainted,
    onExportSucess,
    onDownloadError,
}, ref) => {
    const language = useLanguage();
    const { onExport } = useMasterSeriDrawer(
        true,
        canvasMap,
        {
            imageChangeCount,
            isInitializing,
            language,
            globalScale,
        },
    );
    const {
        exportCanvasRef,
    } = canvasMap;
    const exportRef = useRef({
        currentPipeline: Promise.resolve(),
        pipelineRunning: false,
    });
    const [isDownloading, setDownloading] = useState(false);
    const onDownloadComplete = useCallback(() => {
        setDownloading(false);
    }, []);
    const onExportWithCallback = useCallback(async (param: Parameters<UseCardExport['onExport']>[0]) => {
        return onExport({
            ...param,
            onError: e => {
                if (!isTainted && String(e).includes('Tainted canvases')) {
                    onTainted();
                }
            },
            onSuccess: onExportSucess,
        });
    }, [isTainted, onExport, onExportSucess, onTainted]);
    const { onSave, isPipelineRunning } = useCardExport({
        isTainted,
        isInitializing,
        exportCanvasRef,
        exportRef,
        onExport: onExportWithCallback,
        onDownloadError,
        onDownloadComplete,
    });

    const download = (size?: [number, number]) => {
        if (isDownloading) return;
        if (isTainted) {
            notification.error({
                message: language['prompt.download.tainted.message'],
            });
            return;
        }
        setDownloading(true);
        onSave(size);
        return;
    };
    useImperativeHandle(ref, () => ({
        download,
        isPipelineRunning,
    }));

    if (isTainted) return null;
    return <DownloadButton
        onDownload={download}
        isDownloading={isDownloading}
    />;
});

export type DownloadButton = {
    isDownloading: boolean,
    onDownload?: (size?: [number, number]) => void,
} & Pick<ResolutionPicker, 'onResolutionChange'>;
export const DownloadButton = ({
    isDownloading,
    onDownload,
    onResolutionChange,
}: DownloadButton) => {
    const language = useLanguage();
    const {
        allowHotkey,
        resolution,
    } = useSetting(useShallow(({
        setting: { allowHotkey, resolution },
    }) => ({
        allowHotkey, resolution,
    })));

    return <div className="save-button-container">
        <div id="save-button-waiting" />
        <StyledDownloadButton className="save-button" id="save-button-ready">
            <Tooltip overlay={allowHotkey ? <>Ctrl+S / ⌘+S</> : null}>
                <div className="button-label">
                    <div className="label-content" onClick={() => onDownload?.()}>
                        {isDownloading
                            ? language['button.download.ongoing.label']
                            : language['button.download.label']}
                    </div>
                </div>
            </Tooltip>
            <DownloadResolutionDropdown
                disabled={isDownloading}
                resolution={resolution}
                onResolutionChange={onResolutionChange}
            />
        </StyledDownloadButton>
    </div>;
};

export type DownloadResolutionDropdown = {
    resolution: [number, number],
    disabled?: boolean,
    onResolutionChange?: ResolutionPicker['onResolutionChange'],
}
export const DownloadResolutionDropdown = ({
    resolution,
    disabled,
    onResolutionChange,
}: DownloadResolutionDropdown) => {
    return <Dropdown
        disabled={disabled}
        className="save-button-dropdown"
        placement="bottomRight"
        overlay={<ResolutionPicker
            onResolutionChange={(...args) => {
                forceRefocus();
                onResolutionChange?.(...args);
            }}
        />}
    >
        <ResolutionButton className="resolution-option" onClick={e => e.stopPropagation()}>
            <GatewayOutlined className="resolution-icon" />
            <span className="resolution-overlay">{resolution[1]}</span>
        </ResolutionButton>
    </Dropdown>;
};